// ============================================
// AppErrorBoundary - admin portal
// ---------------------------------------------
// Ported from the student/parent portal, where this has been in place since
// OEL-67. The logic is deliberately identical; only the wording differs.
//
// App.tsx loads all 200-odd admin pages with React.lazy() + <Suspense>. With no
// error boundary in the tree, ANY error thrown while a route renders unmounts
// the whole app and leaves a blank screen.
//
// The usual trigger is a failed dynamic import after a redeploy. Asset
// filenames are content-hashed, so index.html is the only file that says which
// chunks to load. A browser holding a cached index.html keeps asking for the
// PREVIOUS build's chunks, which the deploy deleted - and because the server
// falls back to index.html for anything it cannot find, the request comes back
// as 200 text/html instead of 404. The browser then refuses it:
//
//   Failed to load module script: Expected a JavaScript-or-Wasm module script
//   but the server responded with a MIME type of "text/html".
//   Failed to fetch dynamically imported module: /assets/ClassesPage-<hash>.js
//
// Nothing is wrong with the page itself - it loads again on a hard refresh.
// This boundary does that refresh automatically:
//   - stale-chunk / asset errors -> clear caches and auto-reload ONCE (guarded by a
//     sessionStorage flag so it can never loop) to pull the fresh index.html + chunks.
//   - any other error            -> a visible "Something went wrong" screen with a
//     Reload button, instead of a blank void.
// Strictly additive: on the happy path it renders its children unchanged.
// ============================================
import React from 'react';

const RELOAD_GUARD_KEY = 'app_chunk_reload_at';
// If we already auto-reloaded within this window, don't reload again — show the
// fallback UI instead. Prevents an infinite reload loop when the fresh fetch also fails.
const RELOAD_GUARD_MS = 20_000;

/** True when the error looks like a failed lazy-chunk / stale-asset load. */
function isChunkLoadError(err: unknown): boolean {
  const msg = ((err as any)?.message || String(err || '')).toLowerCase();
  const name = ((err as any)?.name || '').toLowerCase();
  return (
    name === 'chunkloaderror' ||
    msg.includes('failed to fetch dynamically imported module') ||
    msg.includes('error loading dynamically imported module') ||
    msg.includes('importing a module script failed') || // Safari / iOS WebView wording
    msg.includes('loading chunk') ||
    msg.includes('loading css chunk') ||
    msg.includes('unable to preload css')
  );
}

interface Props {
  children: React.ReactNode;
  /**
   * Render a contained fallback instead of the full-screen card. Use this when
   * the boundary wraps one panel (a document viewer, a chart) rather than the
   * whole app: the rest of the page keeps working and the user keeps their
   * navigation. `retry` clears the error and re-mounts the children (OEL-73).
   */
  fallback?: (error: Error, retry: () => void) => React.ReactNode;
  /** Label used in the copyable diagnostic, e.g. "Download Center viewer". */
  label?: string;
  /** Scoped boundaries must not reload the whole app. Defaults to true. */
  autoReload?: boolean;
}
interface State {
  error: Error | null;
  /** true once we've decided to auto-reload — keeps the UI blank-but-brief until reload fires. */
  reloading: boolean;
  /** Component stack from componentDidCatch, shown under Details. */
  stack: string;
  copied: 'idle' | 'done' | 'failed';
}

export class AppErrorBoundary extends React.Component<Props, State> {
  state: State = { error: null, reloading: false, stack: '', copied: 'idle' };

  static getDerivedStateFromError(error: Error): Partial<State> {
    // Decide up-front whether we'll auto-reload, so the first render already shows
    // "Updating…" instead of briefly flashing the error card.
    const willReload = isChunkLoadError(error) && AppErrorBoundary.canAutoReload();
    return { error, reloading: willReload };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    // Keep a breadcrumb for logcat / remote debugging.
    console.error('[AppErrorBoundary]', error, info?.componentStack);
    this.setState({ stack: info?.componentStack || '' });

    // A scoped boundary must never reload the app out from under the user.
    if (this.props.autoReload === false) return;
    if (isChunkLoadError(error) && AppErrorBoundary.canAutoReload()) {
      this.recoverFromStaleAssets();
    }
  }

  /**
   * The one thing support actually needs. Until now the error existed only in a
   * console nobody can open on a school tablet, so every report was "it says
   * something went wrong" and no more (OEL-73).
   */
  private diagnostic(): string {
    const { error, stack } = this.state;
    // The WebView build is the single most useful fact for a crash that only
    // happens on tablets: it decides whether the engine is new enough for the
    // libraries this page loads.
    const engine = (() => {
      try {
        const ua = navigator.userAgent || '';
        const chrome = /Chrome\/(\d+)/.exec(ua)?.[1];
        return `${chrome ? `Chrome/${chrome} ` : ''}${ua}`;
      } catch { return '?'; }
    })();
    return [
      `where: ${this.props.label || 'app'}`,
      // Every tablet report so far has arrived as a photograph of this card, and
      // without the build id it cannot be told whether the device was even
      // running the fix being asked about (OEL-98).
      `build: ${typeof __BUILD_STAMP__ === 'string' ? __BUILD_STAMP__ : 'unknown'}`,
      `page: ${(() => { try { return window.location.href; } catch { return '?'; } })()}`,
      `engine: ${engine}`,
      `screen: ${(() => { try { return `${window.innerWidth}x${window.innerHeight} @${window.devicePixelRatio}x`; } catch { return '?'; } })()}`,
      `error: ${(error as any)?.name || 'Error'}: ${error?.message || String(error)}`,
      stack ? `component stack:${stack}` : '',
      (error as any)?.stack ? `stack:\n${(error as any).stack}` : '',
    ].filter(Boolean).join('\n');
  }

  private handleCopy = async () => {
    const text = this.diagnostic();
    try {
      await navigator.clipboard.writeText(text);
      this.setState({ copied: 'done' });
    } catch {
      // WebViews often refuse the async clipboard; the text is selectable below.
      this.setState({ copied: 'failed' });
    }
  };

  private retry = () => this.setState({ error: null, reloading: false, stack: '', copied: 'idle' });

  /** Only auto-reload if we haven't just done so (loop guard). */
  private static canAutoReload(): boolean {
    try {
      const last = Number(sessionStorage.getItem(RELOAD_GUARD_KEY) || 0);
      return !last || Date.now() - last > RELOAD_GUARD_MS;
    } catch {
      return true; // sessionStorage unavailable → still allow one reload attempt
    }
  }

  /** Clear cached bundles then hard-reload to fetch the current index.html + chunks. */
  private async recoverFromStaleAssets() {
    try { sessionStorage.setItem(RELOAD_GUARD_KEY, String(Date.now())); } catch { /* ignore */ }
    try {
      if (typeof caches !== 'undefined' && caches?.keys) {
        const keys = await caches.keys();
        await Promise.all(keys.map((k) => caches.delete(k)));
      }
    } catch { /* cache clearing is best-effort */ }
    AppErrorBoundary.hardReload();
  }

  /**
   * location.reload() can still be answered from the WebView's HTTP cache, which
   * is exactly the stale index.html we are trying to escape. Requesting a URL it
   * has never seen forces a real network fetch (OEL-73).
   */
  private static hardReload() {
    try {
      const url = new URL(window.location.href);
      url.searchParams.set('_r', String(Date.now()));
      window.location.replace(url.toString());
    } catch {
      window.location.reload();
    }
  }

  private handleReloadClick = () => {
    // Manual retry always reloads (and clears the loop guard so a fresh session starts).
    try { sessionStorage.removeItem(RELOAD_GUARD_KEY); } catch { /* ignore */ }
    AppErrorBoundary.hardReload();
  };

  private handleHomeClick = () => {
    try { sessionStorage.removeItem(RELOAD_GUARD_KEY); } catch { /* ignore */ }
    // The admin app is served from the domain root; BASE_URL is read rather than
    // hardcoding "/" so a future move under a sub-path still lands home.
    const home = (import.meta as any).env?.BASE_URL || '/';
    try { window.location.replace(`${home}?_r=${Date.now()}`); } catch { window.location.reload(); }
  };

  render() {
    const { error, reloading, copied } = this.state;
    if (!error) return this.props.children;

    // A scoped boundary hands control back to the panel that failed, so the rest
    // of the app stays usable instead of being replaced by a full-screen error.
    if (this.props.fallback) return <>{this.props.fallback(error, this.retry)}</>;

    // Keyframes inlined so the spinner animates even if the ui-lib CSS chunk is the
    // very asset that failed to load.
    const spinKeyframes = <style>{'@keyframes aeb-spin{to{transform:rotate(360deg)}}'}</style>;

    // While an auto-reload is in flight, don't flash the error card.
    if (reloading) {
      return (
        <div style={styles.wrap}>
          {spinKeyframes}
          <div style={styles.spinner} />
          <p style={styles.muted}>Updating…</p>
        </div>
      );
    }

    return (
      <div style={styles.wrap}>
        {spinKeyframes}
        <div style={styles.card}>
          <div style={styles.emoji}>😕</div>
          <h2 style={styles.title}>Something went wrong</h2>
          <p style={styles.muted}>
            The page couldn't load. This usually clears up with a reload.
          </p>
          <button type="button" style={styles.button} onClick={this.handleReloadClick}>
            Reload
          </button>
          <button type="button" style={styles.linkBtn} onClick={this.handleHomeClick}>
            Back to home
          </button>

          {/* The error text itself. Without this the only record lives in a
              console nobody can open on a school tablet, so every report was
              "it says something went wrong" and nothing more (OEL-73). */}
          <details style={styles.details}>
            <summary style={styles.summary}>Details for support</summary>
            <pre style={styles.pre}>{this.diagnostic()}</pre>
            <button type="button" style={styles.copyBtn} onClick={this.handleCopy}>
              {copied === 'done' ? 'Copied' : copied === 'failed' ? 'Copy failed — select the text above' : 'Copy'}
            </button>
          </details>
        </div>
      </div>
    );
  }
}

// Inline styles so the fallback never depends on a stylesheet that may itself have
// failed to load. Uses theme tokens with hard fallbacks.
const styles: Record<string, React.CSSProperties> = {
  wrap: {
    display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
    minHeight: '100vh', padding: '24px', textAlign: 'center',
    background: 'var(--app-bg, #f8fafc)', color: 'var(--text-color, #0f172a)',
  },
  card: { maxWidth: 340, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12 },
  emoji: { fontSize: 40, lineHeight: 1 },
  title: { margin: 0, fontSize: 20, fontWeight: 700 },
  muted: { margin: 0, fontSize: 14, color: 'var(--text-muted, #64748b)', lineHeight: 1.5 },
  button: {
    marginTop: 8, padding: '10px 28px', fontSize: 15, fontWeight: 600, cursor: 'pointer',
    color: '#fff', background: 'var(--color-primary-500, #4f46e5)', border: 'none', borderRadius: 10,
  },
  spinner: {
    width: 36, height: 36, borderRadius: '50%', marginBottom: 12,
    border: '3px solid rgba(100,116,139,0.25)', borderTopColor: 'var(--color-primary-400, #818cf8)',
    animation: 'aeb-spin 0.6s linear infinite',
  },
  linkBtn: {
    padding: '6px 12px', fontSize: 13, fontWeight: 600, cursor: 'pointer',
    color: 'var(--color-primary-500, #4f46e5)', background: 'none', border: 'none',
    textDecoration: 'underline',
  },
  details: { marginTop: 12, width: '100%', textAlign: 'left' },
  summary: {
    cursor: 'pointer', fontSize: 12, fontWeight: 600,
    color: 'var(--text-muted, #64748b)', textAlign: 'center',
  },
  pre: {
    marginTop: 8, padding: 10, maxHeight: 200, overflow: 'auto',
    fontSize: 11, lineHeight: 1.45, whiteSpace: 'pre-wrap', wordBreak: 'break-word',
    background: 'rgba(100,116,139,0.10)', borderRadius: 8,
    color: 'var(--text-muted, #475569)',
    // Selectable so the text can still be shared when the clipboard API is blocked.
    userSelect: 'text', WebkitUserSelect: 'text',
  },
  copyBtn: {
    marginTop: 8, padding: '6px 14px', fontSize: 12, fontWeight: 600, cursor: 'pointer',
    color: 'var(--text-color, #0f172a)', background: 'rgba(100,116,139,0.12)',
    border: 'none', borderRadius: 8, width: '100%',
  },
};

export default AppErrorBoundary;
